import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { BookOpen, Plus, Search, Loader2, Download, Upload, Settings, MoreHorizontal, Pencil, Trash2, Copy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CLASSES = [
  { value: 'all', label: 'Toutes les classes' },
  { value: '1', label: '1 - Capitaux' }, { value: '2', label: '2 - Immobilisations' },
  { value: '3', label: '3 - Stocks' }, { value: '4', label: '4 - Tiers' },
  { value: '5', label: '5 - Financiers' }, { value: '6', label: '6 - Charges' },
  { value: '7', label: '7 - Produits' },
];
const TYPES = [
  { value: 'actif', label: 'Actif' }, { value: 'passif', label: 'Passif' },
  { value: 'charge', label: 'Charge' }, { value: 'produit', label: 'Produit' },
];

export default function PlanComptable() {
  const [comptes, setComptes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classe, setClasse] = useState('all');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [form, setForm] = useState({ numero: '', libelle: '', classe: '6', type_compte: 'charge' });
  const [editForm, setEditForm] = useState({ id: '', libelle: '', type_compte: '', notes: '' });
  const [importCsv, setImportCsv] = useState('');
  const [saving, setSaving] = useState(false);
  const [initLoading, setInitLoading] = useState(false);

  const fetchComptes = async () => {
    try {
      const params = {};
      if (classe !== 'all') params.classe = classe;
      if (search) params.search = search;
      const res = await axios.get(`${API_URL}/comptabilite/comptes`, { params });
      setComptes(res.data);
    } catch (e) { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { setLoading(true); fetchComptes(); }, [classe, search]);

  const handleInitPCG = async () => {
    setInitLoading(true);
    try {
      const res = await axios.post(`${API_URL}/comptabilite/comptes/init-pcg`);
      toast.success(res.data.message);
      fetchComptes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setInitLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.numero || !form.libelle) { toast.error('Numéro et libellé requis'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/comptabilite/comptes`, form);
      toast.success('Compte créé');
      setCreateOpen(false);
      setForm({ numero: '', libelle: '', classe: '6', type_compte: 'charge' });
      fetchComptes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const openEdit = (c) => {
    setEditForm({ id: c.id, libelle: c.libelle, type_compte: c.type_compte, notes: c.notes || '' });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/comptabilite/comptes/${editForm.id}`, {
        libelle: editForm.libelle, type_compte: editForm.type_compte, notes: editForm.notes || null,
      });
      toast.success('Compte modifié');
      setEditOpen(false);
      fetchComptes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (c) => {
    const msg = c.is_system ? `Masquer le compte PCG ${c.numero} ?` : `Supprimer le compte ${c.numero} ?`;
    if (!window.confirm(msg)) return;
    try {
      const res = await axios.delete(`${API_URL}/comptabilite/comptes/${c.id}`);
      toast.success(res.data.message);
      fetchComptes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const handleImport = async () => {
    if (!importCsv.trim()) { toast.error('Collez le contenu CSV'); return; }
    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/comptabilite/comptes/import`, { csv: importCsv });
      toast.success(res.data.message);
      setImportOpen(false);
      setImportCsv('');
      fetchComptes();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  const handleExport = async () => {
    try {
      const res = await axios.post(`${API_URL}/comptabilite/comptes/export`, {}, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'plan_comptable.csv'; a.click();
      toast.success('Plan comptable exporté');
    } catch (e) { toast.error('Erreur export'); }
  };

  const handleUploadCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setImportCsv(text);
    setImportOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            <BookOpen className="w-6 h-6 inline mr-2" />Plan comptable
          </h1>
          <p className="text-slate-500">{comptes.length} compte(s)</p>
        </div>
        <div className="flex gap-2">
          {comptes.length === 0 && (
            <Button variant="outline" onClick={handleInitPCG} disabled={initLoading}>
              {initLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Settings className="w-4 h-4 mr-2" /> Initialiser PCG
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="outline"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExport}><Download className="w-4 h-4 mr-2" /> Exporter CSV</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setImportOpen(true)}><Upload className="w-4 h-4 mr-2" /> Importer CSV</DropdownMenuItem>
              <DropdownMenuItem asChild><label className="flex items-center gap-2 cursor-pointer"><Upload className="w-4 h-4" /> Importer fichier<input type="file" className="hidden" accept=".csv" onChange={handleUploadCSV} /></label></DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => { setForm({ numero: '', libelle: '', classe: '6', type_compte: 'charge' }); setCreateOpen(true); }} className="bg-[#0F5156] hover:bg-[#0d464a]">
            <Plus className="w-4 h-4 mr-2" /> Nouveau compte
          </Button>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Rechercher un compte..." className="pl-10" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={classe} onValueChange={setClasse}>
          <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent>{CLASSES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-32">Numéro</TableHead>
                <TableHead>Libellé</TableHead>
                <TableHead className="w-24">Classe</TableHead>
                <TableHead className="w-24">Type</TableHead>
                <TableHead className="w-20">Source</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {comptes.map(c => (
                  <TableRow key={c.id}>
                    <TableCell className="font-mono font-medium">{c.numero}</TableCell>
                    <TableCell>
                      {c.libelle}
                      {c.notes && <span className="text-xs text-slate-400 ml-2">({c.notes})</span>}
                    </TableCell>
                    <TableCell><Badge variant="outline">{c.classe}</Badge></TableCell>
                    <TableCell className="text-sm capitalize">{c.type_compte}</TableCell>
                    <TableCell>{c.is_system ? <Badge className="bg-blue-100 text-blue-700 text-xs">PCG</Badge> : <Badge className="bg-slate-100 text-slate-500 text-xs">Custom</Badge>}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(c)}><Pencil className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(c)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {comptes.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-slate-500">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Aucun compte. Initialisez le PCG ou importez un plan comptable.</p>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouveau compte comptable</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Numéro *</Label><Input placeholder="ex: 411100" value={form.numero} onChange={e => setForm({...form, numero: e.target.value})} /></div>
            <div><Label>Libellé *</Label><Input placeholder="ex: Clients divers" value={form.libelle} onChange={e => setForm({...form, libelle: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Classe</Label><Select value={form.classe} onValueChange={v => setForm({...form, classe: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CLASSES.filter(c => c.value !== 'all').map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Type</Label><Select value={form.type_compte} onValueChange={v => setForm({...form, type_compte: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
            <Button onClick={handleCreate} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Créer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifier le compte</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Libellé</Label><Input value={editForm.libelle} onChange={e => setEditForm({...editForm, libelle: e.target.value})} /></div>
            <div><Label>Type</Label><Select value={editForm.type_compte} onValueChange={v => setEditForm({...editForm, type_compte: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Notes</Label><Input value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})} placeholder="Note optionnelle" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button onClick={handleEdit} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle><Upload className="w-5 h-5 inline mr-2" />Importer un plan comptable</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-500">Collez le contenu CSV (séparateur: point-virgule). Colonnes attendues: <code className="text-xs bg-slate-100 px-1 rounded">numero;libelle;classe;type_compte</code></p>
            <p className="text-xs text-slate-400">Les comptes existants seront mis à jour (libellé). Les nouveaux seront créés.</p>
            <Textarea value={importCsv} onChange={e => setImportCsv(e.target.value)} rows={10} placeholder="numero;libelle;classe;type_compte&#10;401000;Fournisseurs;4;passif&#10;411000;Clients;4;actif" className="font-mono text-xs" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Annuler</Button>
            <Button onClick={handleImport} disabled={saving} className="bg-[#0F5156] hover:bg-[#0d464a]">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Importer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
